import {
  formatCodeableConcept,
  getIdentifier,
  isResource,
  resolveId,
} from '@medplum/core'
import type {
  Annotation,
  Basic,
  Coding,
  DocumentReference,
  Encounter,
  Identifier,
  Organization,
  Patient,
  Practitioner,
  Reference,
  ServiceRequest,
} from '@medplum/fhirtypes'
import {
  addDays,
  differenceInDays,
  differenceInMinutes,
  isFuture,
  isToday,
} from 'date-fns'
import { compact, uniq, uniqBy } from 'lodash-es'
import { z } from 'zod'
import {
  type CodingValue,
  codeableConcept,
  codeFromCodeableConcept,
  getCodingFromCodeableConcept,
  getCodingsFromCodeableConcept,
  hasCodeableConcept,
} from '#src/codeableConcept.ts'
import {
  ASTRANA_PORTAL_AUTHORIZATION_ID_SYSTEM,
  EXTENDED_RESOURCE_SUBSCRIBERS,
  ICD10_SYSTEM,
  NUCC_PROVIDER_TAXONOMY_SYSTEM,
  ORDER_AI_TITLE_SYSTEM,
  ORDER_LABEL_TAG_SYSTEM,
  ORDER_SPECIALTY_SYSTEM,
  ReferralCode,
  SEEN_HEALTH_SYSTEM,
  SNOMED_CT_SYSTEM,
} from '#src/codeSystems.ts'
import { HEALTH_GORILLA_META_SOURCE } from '#src/constants.ts'
import { isInPast } from '#src/dates.ts'
import { Extension, Tag } from '#src/decorators.ts'
import type { ExportFields } from '#src/ExtendedResource.ts'
import { OrderLabel } from '#src/models/orderLabel.ts'
import {
  buildOrderCategoryCoding,
  INTERNAL_ORDER_CODING,
  ORDER_CATEGORY_CODE_SYSTEM,
  ORDER_CODING,
  ORDER_DETAILS,
  ORDER_PRIORITY_TIMEFRAME,
  ORDER_STATUS,
  OrderCategory,
  orderCategoryLabel,
  RetiredOrderCategory,
  type OrderPriorityTimeframe,
  type OrderStatus,
} from '#src/models/orders/constants.ts'
import { ExtendedPractitioner } from '#src/models/practitioner.ts'
import { Signable } from '#src/models/signable.ts'
import { specialtyValue } from '#src/models/specialties.ts'
import type { Subscribeable } from '#src/models/subscribeable.ts'
import { getReferenceString } from '#src/references.ts'
import { dateRangeFilter, type QueryTypes } from '#src/search.ts'
import { SERVICE_REQUEST_IDENTIFIER } from '#src/structureDefinitions.ts'
import { isOfType, now, parseReferenceString } from '#src/utils.ts'

export const ORDER_COMPLETED_DATE_SYSTEM = `${SEEN_HEALTH_SYSTEM}/StructureDefinition/completed-date`
export const ORDER_EFFECTUATED_DATE_SYSTEM = `${SEEN_HEALTH_SYSTEM}/StructureDefinition/effectuated-date`
export const ORDER_COORDINATED_DATE_SYSTEM = `${SEEN_HEALTH_SYSTEM}/StructureDefinition/coordinated-date`
export const ORDER_BUSINESS_STATUS_SYSTEM = `${SEEN_HEALTH_SYSTEM}/StructureDefinition/order-business-status`
export const ORDER_DUE_DATE_SYSTEM = `${SEEN_HEALTH_SYSTEM}/StructureDefinition/order-due-date`
export const ORDER_COLLECTION_DATE_SYSTEM = `${SEEN_HEALTH_SYSTEM}/StructureDefinition/order-collection-date`
export const ORDER_STANDING_SYSTEM = `${SEEN_HEALTH_SYSTEM}/StructureDefinition/order-standing`
export const ORDER_SUBMISSION_STATE_SYSTEM = `${SEEN_HEALTH_SYSTEM}/StructureDefinition/order-submission-state`
export const ORDER_SUBMISSION_ERROR_SYSTEM = `${SEEN_HEALTH_SYSTEM}/StructureDefinition/order-submission-error`
export const ORDER_SUBMISSION_STATE_CHANGED_AT_SYSTEM = `${SEEN_HEALTH_SYSTEM}/StructureDefinition/order-submission-state-changed-at`

/**
 * After this many minutes in `dispatched` without the HG bot writing a
 * terminal signal (healthGorillaIdentifier or splitGroups), an order is
 * "stuck" — the bot crashed, timed out, or its terminal write was lost.
 * Janitors query for `dispatched` orders with `submissionStateChangedAt`
 * older than this and re-request or mark failed. Sized to the Medplum
 * bot's 10-minute async budget plus headroom for clock skew + write
 * propagation.
 */
export const ORDER_SUBMISSION_STUCK_AFTER_MINUTES = 15
/**
 * Extension written by Health Gorilla's `send-to-health-gorilla` Medplum bot
 * when a submitted order requires splitting. The value is a serialized groups string consumed by
 * the `split-order` bot. Presence of this extension drives the async split
 * subscription handler.
 */
export const ORDER_SPLIT_GROUPS_EXTENSION_URL =
  'https://api.healthgorilla.com/fhir/R4/fhir/StructureDefinition/operationoutcome-order-splitting'

/**
 * Lab order submission lifecycle (Health Gorilla path):
 *
 *  - `requested`  — user (or split handler) asked to submit; submit
 *                   subscription has not yet dispatched the HG bot.
 *                   Subscription criterion narrows to this tag, so bot
 *                   intermediate writes do NOT re-fire the subscription
 *                   once we transition to `dispatched`.
 *  - `dispatched` — submit subscription has invoked the HG bot
 *                   (respond-async). Bot owns the order until it writes
 *                   `healthGorillaIdentifier` (success), `splitGroups`
 *                   (split required), or `submissionState=error`.
 *  - `error`      — bot or dispatcher reported failure; UI surfaces this.
 *  - undefined    — terminal success / never submitted.
 *
 * Janitors find stuck `dispatched` orders by combining state with
 * `submissionStateChangedAt` (see ORDER_SUBMISSION_STUCK_AFTER_MINUTES).
 */
export type OrderSubmissionState = 'requested' | 'dispatched' | 'error'

const STANDING_ORDER_CATEGORY_BLACKLIST: OrderCategory[] = [
  OrderCategory.Specialist,
  OrderCategory.Imaging,
  OrderCategory.Lab,
  OrderCategory.Cardiac,
]

export const isStandingOrderEligible = (category: OrderCategory): boolean =>
  !STANDING_ORDER_CATEGORY_BLACKLIST.includes(category)

const orderCategoryIncludes = (
  categories: readonly OrderCategory[],
  category: OrderCategory
): boolean => categories.includes(category)

export class Order extends Signable<ServiceRequest> implements Subscribeable {
  static readonly RESOURCE_TYPE = 'ServiceRequest'

  /**
   * Order statuses representing terminal lifecycle states — the order
   * cannot transition out of these into an actionable state. Used by
   * integrations (e.g. Health Gorilla submission/split subscriptions) to
   * skip processing orders that are no longer eligible for outbound work.
   */
  static readonly TERMINAL_STATUSES: readonly OrderStatus[] = [
    'entered-in-error',
    'revoked',
    'completed',
  ] as const

  static isInstance(resource: unknown): resource is ServiceRequest {
    return isResource(resource) && isOfType(resource, 'ServiceRequest')
  }

  static queryAll(opts: QueryTypes = {}): QueryTypes {
    return {
      ...ALL_QUERY,
      ...opts,
    }
  }

  /**
   * Query for the orders list page. Excludes completed/revoked orders by
   * default to keep the initial fetch bounded — callers that need those
   * statuses must opt in by passing their own `status` or `status:not`.
   */
  static listPageQuery(opts: QueryTypes = {}): QueryTypes {
    const hasExplicitStatus = 'status' in opts || 'status:not' in opts
    const statusDefault: QueryTypes = hasExplicitStatus
      ? {}
      : { 'status:not': 'completed,revoked,entered-in-error' }

    return this.queryAll({
      ...statusDefault,
      ...opts,
    })
  }

  static byParticipant(params: {
    participant: Reference<Patient>
  }): QueryTypes {
    return {
      ...this.queryAll(),
      patient: params.participant,
    }
  }

  static queryOpenByParticipant(participant: Reference<Patient>): QueryTypes {
    return {
      ...this.byParticipant({ participant }),
      'status:not': [...this.TERMINAL_STATUSES],
    }
  }

  static byParticipantUnsigned(participant: Reference<Patient>): QueryTypes {
    return {
      ...this.byParticipant({ participant }),
      status: 'on-hold',
    }
  }

  static byNonDraftOrCancelled(patient?: Reference<Patient>): QueryTypes {
    return {
      ...this.queryAll(),
      patient,
      'status:not': 'draft,entered-in-error,revoked',
    }
  }

  static bySeenIdentifier(identifier: string | undefined): QueryTypes {
    return {
      ...this.queryAll(),
      identifier: {
        system: SERVICE_REQUEST_IDENTIFIER,
        value: identifier ?? '',
      },
    }
  }

  /**
   * Normalize a user-typed string (`SR6976`, `SR-6976`, `sr6976`, `sr-6976`)
   * into a canonical seen identifier (`SR-6976`), or `null` if it doesn't
   * parse — used to gate backend lookups on identifier-like input.
   */
  static parseSeenIdentifier(input: string): string | null {
    const match = input.trim().match(/^sr-?(\d+)$/i)
    return match ? `SR-${match[1]}` : null
  }

  static byAuthorizationId(authorizationId: string | undefined): QueryTypes {
    return {
      ...this.queryAll(),
      identifier: {
        system: ASTRANA_PORTAL_AUTHORIZATION_ID_SYSTEM,
        value: authorizationId ?? '',
      },
    }
  }

  /*
   * Fetch orders by explicit IDs. Uses only `entered-in-error` exclusion
   * (not the `revoked` filter from queryAll) because callers like the visit
   * note sidebar need to show historically linked orders regardless of
   * current lifecycle status.
   */
  static queryByIds(ids: string[]): QueryTypes {
    return {
      _sort: '-_lastUpdated',
      'status:not': 'entered-in-error',
      _id: ids,
      _count: ids.length,
    }
  }

  static queryByEncounter(
    encounter: Reference<Encounter> | string
  ): QueryTypes {
    return {
      ...this.queryAll(),
      encounter,
    }
  }

  static byRequester(practitionerId: string): QueryTypes {
    return {
      ...this.queryAll(),
      requester: { reference: `Practitioner/${practitionerId}` },
    }
  }

  static byPerformer(practitionerId: string): QueryTypes {
    return {
      ...this.queryAll(),
      performer: { reference: `Practitioner/${practitionerId}` },
    }
  }

  // Orders for any of these NUCC specialties.
  static bySpecialties(specialties: readonly Coding[]): QueryTypes {
    return {
      code: specialties
        .map((coding) => `${NUCC_PROVIDER_TAXONOMY_SYSTEM}|${coding.code}`)
        .join(','),
    }
  }

  static byCategory(category: OrderCategory): QueryTypes {
    return { category: `${ORDER_CATEGORY_CODE_SYSTEM}|${category}` }
  }

  // The provider as ordering (requester) or rendering (performer) provider; a
  // vendor Organization is only ever the performer.
  static byProvider(
    provider: Reference<Practitioner | Organization>,
    field: 'requester' | 'performer'
  ): QueryTypes {
    return { [field]: provider }
  }

  static byAuthored(range: { from?: string; to?: string }): QueryTypes {
    return dateRangeFilter('authored', range)
  }

  static categorySchema = z.nativeEnum(OrderCategory)

  // Accepts the code already stored on an order being edited, including a
  // retired one. New orders validate against categorySchema.
  static storedCategorySchema = z.union([
    z.nativeEnum(OrderCategory),
    z.nativeEnum(RetiredOrderCategory),
  ])

  constructor({
    participant,
    status,
    category,
    description,
    preferredVendor,
    priority,
    ...rest
  }: Omit<
    ServiceRequest,
    'resourceType' | 'category' | 'status' | 'subject' | 'intent' | 'priority'
  > & {
    participant: Reference<Patient>
    status?: OrderStatus
    category?: OrderCategory
    description?: string
    preferredVendor?: string
    priority?: OrderPriorityTimeframe | null
  }) {
    super({
      status: 'draft',
      subject: participant,
      code: codeableConcept(ORDER_CODING),
      intent: 'order',
      authoredOn: now(),
      ...rest,
    })
    this.status = status ?? 'draft'
    if (category) {
      this.category = category
    }
    if (description) {
      this.description = description
    }
    if (preferredVendor) {
      this.preferredVendor = preferredVendor
    }
    if (priority) {
      this.priority = priority
    }
  }

  get seenIdentifier(): string | undefined {
    return getIdentifier(this.resource, SERVICE_REQUEST_IDENTIFIER)
  }
  set seenIdentifier(identifier: string) {
    this.setIdentifier(SERVICE_REQUEST_IDENTIFIER, identifier)
  }

  /**
   * Identifier assigned by Health Gorilla when an order is accepted by the
   * lab vendor. Presence indicates the order has already been submitted
   * upstream — used by the dispatch handler to short-circuit duplicate
   * submissions when retries land on an already-accepted order.
   */
  get healthGorillaIdentifier(): string | undefined {
    return getIdentifier(this.resource, HEALTH_GORILLA_META_SOURCE)
  }

  get status(): OrderStatus {
    if (this.resource.status === 'active' && this.businessStatus) {
      return this.businessStatus as OrderStatus
    }
    return this.resource.status as OrderStatus
  }

  set status(status: OrderStatus) {
    this.businessStatus = undefined
    if (status === 'effectuated') {
      this.resource.status = 'active'
      this.businessStatus = 'effectuated'
    } else if (status === 'coordinated') {
      this.resource.status = 'active'
      this.businessStatus = 'coordinated'
    } else {
      this.resource.status = status
    }
  }

  get priority(): OrderPriorityTimeframe | undefined {
    const fhirPriority = this.resource.priority
    if (!fhirPriority) {
      return undefined
    }

    const occurrenceEnd = this.resource.occurrencePeriod?.end
    if (!occurrenceEnd) {
      if (fhirPriority === 'stat') {
        return 'stat-1'
      } else if (fhirPriority === 'urgent') {
        return 'urgent-7'
      } else if (fhirPriority === 'asap') {
        return 'asap-14'
      } else if (fhirPriority === 'routine') {
        return 'routine-30'
      }

      return `${fhirPriority}-30` as OrderPriorityTimeframe
    }

    const endDate = new Date(occurrenceEnd)
    const startDate = this.resource.occurrencePeriod?.start
      ? new Date(this.resource.occurrencePeriod.start)
      : new Date()
    const daysDiff = differenceInDays(endDate, startDate)

    const matchingKey = Object.entries(ORDER_PRIORITY_TIMEFRAME).find(
      ([_, config]) =>
        config.priority === fhirPriority && Math.abs(config.days - daysDiff) < 2
    )?.[0]

    return (matchingKey ?? `${fhirPriority}-30`) as OrderPriorityTimeframe
  }

  set priority(priorityTimeframe: OrderPriorityTimeframe | undefined | null) {
    if (!priorityTimeframe) {
      this.resource.priority = undefined
      this.resource.occurrencePeriod = undefined
      return
    }

    const config = ORDER_PRIORITY_TIMEFRAME[priorityTimeframe]
    this.resource.priority = config.priority
    const authored = this.authoredOn ?? now()
    this.resource.occurrencePeriod = {
      start: authored,
      end: addDays(authored, config.days).toISOString(),
    }
  }

  get urgent(): boolean {
    const fhirPriority = this.resource.priority
    return fhirPriority === 'urgent' || fhirPriority === 'stat'
  }

  get priorityText(): string | undefined {
    const priority = this.priority
    if (!priority) {
      return undefined
    }
    return ORDER_PRIORITY_TIMEFRAME[priority].label
  }

  get category(): OrderCategory {
    return codeFromCodeableConcept(
      this.resource.category,
      ORDER_CATEGORY_CODE_SYSTEM
    ) as OrderCategory
  }

  set category(category) {
    const coding = buildOrderCategoryCoding(category)
    this.resource.category ??= []
    const next = this.resource.category.filter(
      (c) => !c.coding?.some((c) => c.system === ORDER_CATEGORY_CODE_SYSTEM)
    )
    next.push({
      coding: [coding],
    })
    this.resource.category = next
  }

  get title(): string | undefined {
    return this.resource.text?.div ?? this.aiTitle
  }

  set title(title: string | undefined) {
    this.resource.text = title
      ? {
          status: 'generated',
          div: title,
        }
      : undefined
  }

  get titleWithId(): string {
    let displayTitle = this.seenIdentifier ?? ''
    if (this.title) {
      displayTitle += ` ${this.title}`
    } else if (this.aiTitle) {
      displayTitle += ` ${this.aiTitle}`
    } else if (this.categoryText) {
      displayTitle += ` ${this.categoryText}`
    }
    return displayTitle
  }

  get requiresRecordsRetrieval(): boolean {
    return (
      this.category === OrderCategory.Specialist ||
      this.category === OrderCategory.Imaging
    )
  }

  get supportsAuthorizationLink(): boolean {
    return orderCategoryIncludes(
      [
        OrderCategory.Specialist,
        OrderCategory.Imaging,
        OrderCategory.DME,
        OrderCategory.HomeHealth,
        OrderCategory.Cardiac,
      ],
      this.category
    )
  }

  get requiresAppointment(): boolean {
    if (this.category === OrderCategory.Lab) {
      return (
        !!this.collectionDate &&
        isFuture(this.collectionDate) &&
        !isToday(this.collectionDate)
      )
    }

    return orderCategoryIncludes(
      [
        OrderCategory.Specialist,
        OrderCategory.Imaging,
        OrderCategory.SocialWork,
        OrderCategory.Rehabilitation,
        OrderCategory.MassageTherapy,
        OrderCategory.SpeechTherapy,
        OrderCategory.RecreationalTherapy,
      ],
      this.category
    )
  }

  get shouldAutoAssign(): boolean {
    return !orderCategoryIncludes(
      [
        OrderCategory.Specialist,
        OrderCategory.Imaging,
        OrderCategory.Lab,
        OrderCategory.Cardiac,
        OrderCategory.EscortServices,
        OrderCategory.DME,
      ],
      this.category
    )
  }

  get requiresVendorOrder(): boolean {
    return orderCategoryIncludes(
      [
        OrderCategory.DME,
        OrderCategory.EscortServices,
        OrderCategory.Transportation,
        OrderCategory.FacilitiesAndSafety,
      ],
      this.category
    )
  }

  get requiresDMEDocumentation(): boolean {
    return this.category === OrderCategory.DME
  }

  get requiresHomeCareDocumentation(): boolean {
    return this.category === OrderCategory.HomeCare
  }

  get coordinatedBy(): string | undefined {
    const coordinatedDate = this.coordinatedDate
    if (!coordinatedDate) {
      return undefined
    }

    return addDays(coordinatedDate, 7)?.toISOString()
  }

  get coordinatedByIndicator(): 'stat' | 'urgent' | undefined {
    const coordinatedBy = this.coordinatedBy
    if (
      !coordinatedBy ||
      this.status === 'completed' ||
      this.status === 'revoked'
    ) {
      return undefined
    }

    const daysRemaining = differenceInDays(coordinatedBy, now())
    if (daysRemaining <= 2) {
      return 'stat'
    } else if (daysRemaining <= 4) {
      return 'urgent'
    } else {
      return undefined
    }
  }

  get authoredOn(): string | undefined {
    return this.resource.authoredOn
  }

  @Tag({
    system: ORDER_STANDING_SYSTEM,
  })
  _standing?: Coding

  get standing(): boolean {
    return !!this._standing
  }

  set standing(isStanding: boolean) {
    if (isStanding && ['coordinated', 'effectuated'].includes(this.status)) {
      this.status = 'active'
    }

    this._standing = isStanding
      ? codeableConcept({
          system: ORDER_STANDING_SYSTEM,
          code: 'standing',
        })
      : undefined
  }

  get isStandingEligible(): boolean {
    return isStandingOrderEligible(this.category)
  }

  get requisition(): Identifier | undefined {
    return this.resource.requisition
  }

  static DAYS_TO_COORDINATE_AFTER_SIGNATURE = 7

  @Extension({
    url: ORDER_DUE_DATE_SYSTEM,
    kind: 'valueDateTime',
  })
  _dueDate: string | undefined

  set dueDate(dueDate: Date | string | undefined) {
    if (dueDate) {
      this._dueDate =
        typeof dueDate === 'string' ? dueDate : dueDate.toISOString()
    } else {
      this._dueDate = undefined
    }
  }

  get dueDate(): string | undefined {
    if (this._dueDate) {
      return this._dueDate
    }

    if (!this.signedDate) {
      return undefined
    }
    return addDays(
      this.signedDate,
      Order.DAYS_TO_COORDINATE_AFTER_SIGNATURE
    ).toISOString()
  }

  get isDue(): boolean {
    const dueDate = this.dueDate
    if (!dueDate || this.status !== 'active') {
      return false
    }
    return isToday(dueDate) || isInPast(dueDate)
  }

  get isOverdue(): boolean {
    const dueDate = this.dueDate
    if (!dueDate || this.status !== 'active') {
      return false
    }
    return isInPast(dueDate)
  }

  @Extension({
    url: ORDER_COLLECTION_DATE_SYSTEM,
    kind: 'valueDateTime',
  })
  _collectionDate: string | undefined

  set collectionDate(collectionDate: Date | string | undefined) {
    if (collectionDate) {
      this._collectionDate =
        typeof collectionDate === 'string'
          ? collectionDate
          : collectionDate.toISOString()
    } else {
      this._collectionDate = undefined
    }
  }

  get collectionDate(): string | undefined {
    if (this._collectionDate) {
      return this._collectionDate
    }
    return undefined
  }

  get authorizationId(): string | undefined {
    return getIdentifier(this.resource, ASTRANA_PORTAL_AUTHORIZATION_ID_SYSTEM)
  }

  set authorizationId(authorizationId: string | undefined) {
    if (authorizationId) {
      this.setIdentifier(
        ASTRANA_PORTAL_AUTHORIZATION_ID_SYSTEM,
        authorizationId
      )
    } else {
      this.removeIdentifier(ASTRANA_PORTAL_AUTHORIZATION_ID_SYSTEM)
    }
  }

  get participant(): Reference<Patient> {
    return this.resource.subject as Reference<Patient>
  }

  set participant(participant: Reference<Patient>) {
    this.resource.subject = participant
  }

  get requester(): Reference<Practitioner> {
    return this.resource.requester as Reference<Practitioner>
  }

  set requester(requester: Reference<Practitioner> | undefined) {
    this.resource.requester = requester
  }

  get performer(): Reference<Practitioner | Organization> {
    return this.resource.performer?.[0] as Reference<
      Practitioner | Organization
    >
  }

  get performerPractitioner(): Reference<Practitioner> | undefined {
    const performer = this.resource.performer?.[0]
    return isOfType(performer, 'Practitioner') ? performer : undefined
  }

  set performer(assignee: Reference<Practitioner | Organization> | undefined) {
    this.resource.performer = assignee ? [assignee] : undefined
  }

  get searchKeywordsIdentifiers(): string[] {
    return compact([
      this.authorizationId,
      this.seenIdentifier,
      this.seenIdentifier?.replace(/\D/g, ''),
    ])
  }

  get searchKeywords(): string[] {
    return compact([
      ...this.searchKeywordsIdentifiers,
      this.aiTitle,
      this.categoryCodeDisplay,
      this.orderDetailsText,
      ...this.reasonTexts,
      this.code?.coding?.map((c) => c.display).join(' '),
      this.specialty?.display,
      this.performer?.display,
      this.requester?.display,
    ])
  }

  get categoryText(): string | undefined {
    return uniq(this.resource.category?.map(formatCodeableConcept)).join(', ')
  }

  get categoryCode(): OrderCategory | undefined {
    return getCodingFromCodeableConcept(
      this.resource.category,
      ORDER_CATEGORY_CODE_SYSTEM
    )?.code as OrderCategory | undefined
  }

  get categoryCodeDisplay(): string | undefined {
    const code = this.categoryCode
    if (!code) {
      return undefined
    }
    return orderCategoryLabel(code)
  }

  get reasonCode(): ServiceRequest['reasonCode'] {
    return this.resource.reasonCode ?? []
  }

  set reasonCode(reasonCode: ServiceRequest['reasonCode']) {
    this.resource.reasonCode = reasonCode
  }

  get icd10Codes(): Coding[] | undefined {
    return getCodingsFromCodeableConcept(this.reasonCode, ICD10_SYSTEM)
  }

  get reasonTexts(): string[] {
    return compact(
      this.reasonCode?.flatMap(
        (c) => c.text ?? c.coding?.map((c) => c.display ?? c.code)
      )
    )
  }

  get orderDetails(): ServiceRequest['orderDetail'] {
    return this.resource.orderDetail
  }

  get orderDetailsText(): string | undefined {
    return this.orderDetails
      ?.map((detail) => detail.text ?? '')
      .join('\n')
      ?.trim()
  }

  get patientInstructions(): ServiceRequest['patientInstruction'] {
    return this.resource.patientInstruction
  }

  get code(): ServiceRequest['code'] {
    return this.resource.code
  }

  set code(code: ServiceRequest['code']) {
    this.resource.code = code
  }

  get encounter(): Reference<Encounter> | undefined {
    return this.resource.encounter
  }

  updateCodesBySystem(system: string, codes: CodingValue[]): void {
    this.code = codeableConcept([
      ...((this.code?.coding?.filter((c) => c.system !== system) ??
        []) as CodingValue[]),
      ...codes,
    ])
  }

  codesBySystem(system: string): Coding[] {
    return getCodingsFromCodeableConcept(this.code, system) ?? []
  }

  get displayCodes(): Coding[] {
    return (
      getCodingsFromCodeableConcept(this.code)?.filter(
        (c) =>
          c.code !== ORDER_CODING.code &&
          c.code !== INTERNAL_ORDER_CODING.code &&
          c.code !== ReferralCode.Imaging
      ) ?? []
    )
  }

  // Specialty is NUCC-coded (system NUCC_PROVIDER_TAXONOMY_SYSTEM) so it matches
  // the provider/roster vocabulary. Falls back to the legacy
  // ORDER_SPECIALTY_SYSTEM coding for orders written before the cutover (until
  // the backfill runs).
  get specialty(): Coding | undefined {
    return (
      getCodingFromCodeableConcept(
        this.resource.code,
        NUCC_PROVIDER_TAXONOMY_SYSTEM
      ) ??
      getCodingFromCodeableConcept(this.resource.code, ORDER_SPECIALTY_SYSTEM)
    )
  }

  // The specialty as a stable picker value (NUCC code when resolvable, raw
  // text otherwise) — the vocabulary provider qualifications and the request
  // form's specialty field match against.
  get specialtyValue(): string | undefined {
    const coding = this.specialty
    if (!coding) {
      return undefined
    }
    return specialtyValue({ coding: [coding], text: coding.display })
  }

  set specialty(specialty: Coding | undefined) {
    // Drop both the NUCC and legacy specialty codings, then write the new one.
    const next =
      this.resource.code?.coding?.filter(
        (c) =>
          c.system !== NUCC_PROVIDER_TAXONOMY_SYSTEM &&
          c.system !== ORDER_SPECIALTY_SYSTEM
      ) ?? []
    if (specialty) {
      next.push(specialty)
    }
    this.resource.code = codeableConcept(next as CodingValue[])
  }

  get date(): Date | undefined {
    const dateString =
      this.resource.authoredOn ??
      this.signedDate ??
      this.resource.meta?.lastUpdated
    if (!dateString) {
      return undefined
    }
    return new Date(dateString)
  }

  get notes(): Annotation[] {
    return this.resource.note ?? []
  }

  get documentReferences(): Reference<DocumentReference>[] {
    return (
      this.resource.supportingInfo?.filter((s) =>
        isOfType(s, 'DocumentReference')
      ) ?? []
    )
  }

  get basedOn(): Reference<ServiceRequest>[] {
    return (this.resource.basedOn?.filter((b) =>
      isOfType(b, 'ServiceRequest')
    ) ?? []) as Reference<ServiceRequest>[]
  }

  set basedOn(basedOn: Reference<ServiceRequest>[]) {
    this.resource.basedOn = basedOn
  }

  addBasedOn(basedOn: Reference<ServiceRequest>): void {
    this.resource.basedOn = uniqBy(
      [...this.basedOn, basedOn],
      getReferenceString
    )
  }

  protected getOrderDetail(coding: Coding): string {
    return (
      this.resource.orderDetail?.find(
        (detail) => getCodingFromCodeableConcept(detail)?.code === coding.code
      )?.text ?? ''
    )
  }

  protected setOrderDetail(coding: Coding, text: string): void {
    if (!this.resource.orderDetail) {
      this.resource.orderDetail = []
    }
    const index = this.resource.orderDetail.findIndex(
      (detail) => getCodingFromCodeableConcept(detail)?.code === coding.code
    )

    const detailEntry = codeableConcept(coding as CodingValue)
    detailEntry.text = text

    if (index === -1) {
      this.resource.orderDetail.push(detailEntry)
    } else {
      this.resource.orderDetail[index] = detailEntry
    }
  }

  get description(): string {
    return this.getOrderDetail(ORDER_DETAILS.description)
  }

  set description(value: string) {
    this.setOrderDetail(ORDER_DETAILS.description, value)
  }

  get preferredVendor(): string {
    return this.getOrderDetail(ORDER_DETAILS.preferredVendor)
  }

  set preferredVendor(value: string) {
    this.setOrderDetail(ORDER_DETAILS.preferredVendor, value)
  }

  revoke(): void {
    this.status = 'revoked'
  }

  // Order coordination means that the services / requests described in the order have been scheduled, purchased, or otherwise coordinated.
  coordinate(options: { date?: string } = {}): void {
    this.coordinatedDate = options.date ?? now()
    this.status = 'coordinated'
  }

  // Reverts coordination, returning the order to active status.
  uncoordinate(): void {
    this.coordinatedDate = undefined
    this.status = 'active'
  }

  // Order effectuation means that the service(s) described in the order have been rendered.
  // This is distinct from 'completed', as there may be associated follow-up tasks (e.g. records retrieval) that are not yet complete at this stage.
  effectuate(options: { date?: string } = {}): void {
    this.effectuatedDate = options.date ?? now()
    this.status = 'effectuated'
  }

  complete(options: { date?: string } = {}): void {
    this.completedDate = options.date ?? now()
    this.status = 'completed'
  }

  updateStatus(newStatus: OrderStatus, options: { date?: string } = {}): void {
    switch (newStatus) {
      case 'coordinated':
        this.coordinate(options)
        break
      case 'effectuated':
        this.effectuate(options)
        break
      case 'completed':
        this.complete(options)
        break
      case 'revoked':
        this.revoke()
        break
      default:
        this.status = newStatus
    }
  }

  get isEditable(): boolean {
    return [
      'draft',
      'on-hold',
      'active',
      'coordinated',
      'effectuated',
    ].includes(this.status)
  }

  isFieldEditable(
    field:
      | 'performer'
      | 'category'
      | 'participant'
      | 'requester'
      | 'description'
      | 'vendor'
      | 'urgent'
      | 'priority'
      | 'standing'
  ): boolean {
    if (
      field === 'participant' ||
      field === 'category' ||
      field === 'requester' ||
      field === 'description' ||
      field === 'vendor'
    ) {
      return ['draft', 'on-hold'].includes(this.status)
    }

    return [
      'draft',
      'on-hold',
      'active',
      'coordinated',
      'effectuated',
    ].includes(this.status)
  }

  get isRevokable(): boolean {
    return [
      'draft',
      'on-hold',
      'active',
      'coordinated',
      'effectuated',
    ].includes(this.status)
  }

  protected afterSignHook(): void {
    this.status = 'active'
  }

  @Extension({
    url: ORDER_EFFECTUATED_DATE_SYSTEM,
    kind: 'valueDateTime',
  })
  effectuatedDate: string | undefined

  get isEffectuated(): boolean {
    return !!this.effectuatedDate
  }

  @Extension({
    url: ORDER_COMPLETED_DATE_SYSTEM,
    kind: 'valueDateTime',
  })
  completedDate: string | undefined

  get isCompleted(): boolean {
    return this.status === 'completed'
  }

  /**
   * True when the order is in a terminal lifecycle status — completed,
   * revoked, or entered-in-error. Outbound integrations should bail on
   * terminal orders rather than attempting further processing.
   */
  get isTerminalStatus(): boolean {
    return (Order.TERMINAL_STATUSES as readonly OrderStatus[]).includes(
      this.status
    )
  }

  @Extension({
    url: ORDER_COORDINATED_DATE_SYSTEM,
    kind: 'valueDateTime',
  })
  coordinatedDate: string | undefined

  get isCoordinated(): boolean {
    return !!this.coordinatedDate
  }

  @Extension({
    url: ORDER_BUSINESS_STATUS_SYSTEM,
    kind: 'valueString',
  })
  businessStatus?: OrderStatus

  @Extension({
    url: ORDER_AI_TITLE_SYSTEM,
    kind: 'valueString',
  })
  aiTitle?: string

  @Tag({
    system: ORDER_SUBMISSION_STATE_SYSTEM,
  })
  _submissionState?: Coding

  get submissionState(): OrderSubmissionState | undefined {
    return this._submissionState?.code as OrderSubmissionState | undefined
  }

  set submissionState(state: OrderSubmissionState | undefined) {
    this._submissionState = state
      ? { system: ORDER_SUBMISSION_STATE_SYSTEM, code: state }
      : undefined
  }

  @Extension({
    url: ORDER_SUBMISSION_ERROR_SYSTEM,
    kind: 'valueString',
  })
  submissionError?: string

  @Extension({
    url: ORDER_SUBMISSION_STATE_CHANGED_AT_SYSTEM,
    kind: 'valueDateTime',
  })
  submissionStateChangedAt?: string

  /**
   * True while a submit request is in any non-terminal state
   * (`requested` or `dispatched`). UI surfaces this as "submitting".
   * Kept as a single getter so React call sites do not need to know
   * about the two-phase lifecycle.
   */
  get isSubmitting(): boolean {
    return (
      this.submissionState === 'requested' ||
      this.submissionState === 'dispatched'
    )
  }

  /**
   * True after the submit subscription has invoked the HG bot. The bot
   * owns the order from this point until it writes a terminal signal
   * (healthGorillaIdentifier, splitGroups, or submissionState=error).
   */
  get isDispatched(): boolean {
    return this.submissionState === 'dispatched'
  }

  get hasSubmissionError(): boolean {
    return this.submissionState === 'error'
  }

  /**
   * True when the order has been in `dispatched` past the bot's expected
   * runtime budget and is presumed stuck. Janitor entrypoint.
   */
  get isSubmissionStuck(): boolean {
    if (!this.isDispatched) {
      return false
    }
    if (!this.submissionStateChangedAt) {
      return false
    }
    return (
      differenceInMinutes(
        new Date(),
        new Date(this.submissionStateChangedAt)
      ) >= ORDER_SUBMISSION_STUCK_AFTER_MINUTES
    )
  }

  /**
   * Initial transition: user pressed submit, or the split handler
   * enqueued a child. The submit subscription criterion matches on this
   * tag; the handler atomically advances to `dispatched` before invoking
   * the bot, which closes the duplicate-dispatch race because the bot's
   * own intermediate writes (RequestGroup, debug DocumentReferences) no
   * longer match the subscription criterion.
   */
  markRequested(): void {
    this.submissionState = 'requested'
    this.submissionError = undefined
    this.submissionStateChangedAt = now()
  }

  /**
   * Subscription-only transition: dispatched to the HG send bot.
   */
  markDispatched(): void {
    this.submissionState = 'dispatched'
    this.submissionError = undefined
    this.submissionStateChangedAt = now()
  }

  markSubmissionSucceeded(): void {
    this.submissionState = undefined
    this.submissionError = undefined
  }

  markSubmissionFailed(error: string): void {
    this.submissionState = 'error'
    this.submissionError = error
    this.submissionStateChangedAt = now()
  }

  @Extension({
    url: ORDER_SPLIT_GROUPS_EXTENSION_URL,
    kind: 'valueString',
  })
  splitGroups?: string

  get isSplittingRequired(): boolean {
    return !!this.splitGroups
  }

  @Tag({
    system: ORDER_LABEL_TAG_SYSTEM,
    allowMultiple: true,
  })
  _labels?: Coding[]

  get labels(): OrderLabel['referenceString'][] {
    return compact(
      this._labels
        ?.map((l) => l.code)
        .filter((c) => OrderLabel.isValidReferenceString(c))
    )
  }

  get labelReferences(): Reference<Basic>[] {
    return this.labels
      .map((l) => parseReferenceString(l))
      .filter((l): l is Reference<Basic> => l !== undefined)
  }

  set labels(labels: OrderLabel['referenceString'][]) {
    if (labels.length === 0) {
      this._labels = undefined
      return
    }
    this._labels = labels.map((l) => ({
      code: l,
      system: ORDER_LABEL_TAG_SYSTEM,
    }))
  }

  @Extension({
    url: EXTENDED_RESOURCE_SUBSCRIBERS,
    kind: 'valueReference',
    allowMultiple: true,
  })
  subscribers_?: Reference<Practitioner>[]

  get subscribers(): Reference<Practitioner>[] {
    return this.subscribers_ ?? []
  }

  set subscribers(subscribers: Reference<Practitioner>[]) {
    if (subscribers.length === 0) {
      this.subscribers_ = undefined
    } else {
      this.subscribers_ = uniqBy(subscribers, (s) => getReferenceString(s))
    }
  }

  addSubscriber(subscriber: Reference<Practitioner>): void {
    this.subscribers = uniqBy(
      [...this.subscribers, subscriber],
      getReferenceString
    )
  }

  removeSubscriber(subscriber: Reference<Practitioner>): void {
    this.subscribers = this.subscribers.filter(
      (s) => s.reference !== subscriber.reference
    )
  }

  isSubscriber(practitioner: Reference<Practitioner>): boolean {
    return this.subscribers.some((s) => s.reference === practitioner.reference)
  }

  isAssociatedWithPractitioner(
    practitioner: Reference<Practitioner> | ExtendedPractitioner
  ): boolean {
    const practitionerRef =
      practitioner instanceof ExtendedPractitioner
        ? practitioner.reference
        : practitioner
    const refString = getReferenceString(practitionerRef)

    return this.associatedPractitioners.some(
      (r) => getReferenceString(r) === refString
    )
  }

  get associatedPractitioners(): Reference<Practitioner>[] {
    return uniqBy(
      compact([
        ...this.subscribers,
        this.performerPractitioner,
        this.requester,
        this.signedBy,
      ]),
      getReferenceString
    )
  }

  isRelated(other: Order): boolean {
    if (this.category !== other.category) {
      return false
    }

    if (
      this.category === OrderCategory.Specialist ||
      this.category === OrderCategory.Imaging
    ) {
      const samePerformer =
        this.performer &&
        other.performer &&
        this.performer.reference === other.performer.reference
      const sameSpecialty =
        (this.specialty &&
          other.specialty &&
          this.specialty.code === other.specialty.code) ??
        false
      const sameReasonCodes =
        this.reasonCode?.some((c) =>
          other.reasonCode?.some((c2) =>
            c.coding?.some((c3) => {
              const c2Code = c2.coding?.[0]?.code
              const c3Code = c3.code
              return c2Code && c3Code && c3Code === c2Code
            })
          )
        ) ?? false
      const requesterIsPerformer =
        (this.performer &&
          other.requester &&
          this.performer.reference === other.requester.reference) ||
        (this.requester &&
          other.performer &&
          this.requester.reference === other.performer.reference)

      return (
        samePerformer ||
        sameSpecialty ||
        sameReasonCodes ||
        requesterIsPerformer
      )
    }

    if (this.category === OrderCategory.Lab) {
      const hasOverlappingTests = hasCodeableConcept(
        this.resource.code,
        other.resource.code
      )

      const isBasedOn = this.basedOn.some(
        (b) => getReferenceString(b) === getReferenceString(other.reference)
      )

      return hasOverlappingTests || isBasedOn
    }

    return true
  }

  protected getExportFields(): ExportFields {
    const specialty = this.specialty
    return {
      id: this.id,
      status: ORDER_STATUS[this.status],
      category: this.category,
      title: this.title,
      priority: this.priority,
      description: this.description,
      preferredVendor: this.preferredVendor,
      patientId: resolveId(this.participant),
      requesterId: resolveId(this.requester),
      requesterDisplay: this.requester?.display ?? '',
      assigneeId: resolveId(this.performer),
      assigneeDisplay: this.performer?.display ?? '',
      authoredOn: this.authoredOn,
      date: this.date,
      dueDate: this.dueDate,
      isOverdue: this.isOverdue,
      isStanding: this.standing,
      signedDate: this.signedDate,
      signedBy: this.signedBy,
      coordinatedDate: this.coordinatedDate,
      effectuatedDate: this.effectuatedDate,
      completedDate: this.completedDate,
      labels: this.labels,
      specialty: specialty?.code ?? specialty?.display,
    }
  }
}

const ALL_QUERY: QueryTypes = {
  // Ascending _lastUpdated lets Medplum use cursor-based pagination,
  // avoiding the 10k offset cap on large result sets.
  // https://www.medplum.com/docs/search/paginated-search
  _sort: '_lastUpdated',
  'status:not': 'entered-in-error',
  _count: 1000,
  // Ignore Diagnostic procedure
  // Health Gorilla expects Lab orders to have a parent ServiceRequest with each test having a child ServiceRequest categorized as Diagnostic procedure
  'category:not': `${SNOMED_CT_SYSTEM}|103693007`,
}
